// S8g (2026-06-03) — Block clipboard pure helpers.
//
// Two-step model:
//
//   1. SNAPSHOT — take a deep-clone of the selected blocks for the
//      clipboard. Performed at copy/cut time. Cross-references
//      between blocks IN the snapshot are preserved (by original
//      id); cross-references to blocks OUTSIDE the snapshot are
//      stripped (so a single-block copy doesn't carry dangling
//      pointers to its old neighbors).
//
//   2. MATERIALIZE — produce the pasted blocks with fresh UUIDs.
//      Performed at paste time. The id-remap table is built first
//      (old-id → new-id) so all internal references (graph edges
//      AND data.*BlockId runtime refs) update consistently.
//
// This file is engine-behavior-neutral: it doesn't touch the
// engine, doesn't mutate the source blocks, doesn't normalize
// saved data. UI-only state plumbing for copy/cut/paste.

import type { Block } from '../../types';
import { BlockType } from '../../types';

/** Per-block-type set of `data.*` fields that carry block-id
 *  pointers (runtime refs). Mirrors RUNTIME_REF_FIELDS_FOR in
 *  ./blockDependencies.ts — kept independent so adding a new
 *  clipboard-aware runtime-ref slot doesn't require touching the
 *  Dependencies panel and vice versa. The two registries SHOULD
 *  match in practice; tests pin the FTMO-template-relevant fields
 *  on both sides.
 */
const RUNTIME_REF_FIELDS_FOR_CLIPBOARD: Record<string, readonly string[]> = {
  [BlockType.ENTRY]: ['qtyCalculatorBlockId', 'slCalculatorBlockId', 'tpCalculatorBlockId'],
  [BlockType.EXPRESSION]: ['leftInputBlockId', 'rightInputBlockId'],
};

const EMPTY: readonly string[] = Object.freeze([]);

/** Snapshot payload — what `sanitizeBlocksForClipboard` returns and
 *  what `materializePaste` consumes. Lives in BlockCanvas state
 *  between copy and paste. The shape is deliberately
 *  serialization-friendly (plain objects, no live React refs) so a
 *  future enhancement could persist the clipboard to localStorage
 *  without redesign. */
export interface ClipboardPayload {
  /** The deep-cloned blocks. All cross-references that pointed
   *  OUTSIDE the snapshot have been stripped; references between
   *  blocks IN the snapshot still use the ORIGINAL ids (the
   *  materialize step remaps them). */
  blocks: Block[];
  /** The set of original ids that were captured — used by
   *  materialize to know which references to remap. */
  originalIds: string[];
}

/** Build a snapshot of the selected blocks suitable for the
 *  clipboard. Skips STRATEGY_CONFIG (matches handleDuplicateSelected
 *  policy — singleton block, never copyable). Returns null if the
 *  selection ultimately contains nothing copyable. */
export function sanitizeBlocksForClipboard(
  allBlocks: Block[],
  selectedIds: readonly string[],
): ClipboardPayload | null {
  const selectedSet = new Set(selectedIds);
  const copyable = allBlocks.filter(b =>
    selectedSet.has(b.id) && b.type !== BlockType.STRATEGY_CONFIG
  );
  if (copyable.length === 0) return null;

  const copyableIds = new Set(copyable.map(b => b.id));

  // Deep-clone via JSON round-trip — matches the pattern used by
  // handleDuplicateSelected (BlockCanvas.tsx:544). This loses Date
  // objects (metadata.created/modified) and any functions; the
  // materialize step recreates fresh metadata so the loss is
  // intentional. structuredClone would preserve Dates but isn't
  // universally available in old build targets; the JSON path is
  // the existing convention.
  const blocks = copyable.map(b => {
    const cloned: Block = JSON.parse(JSON.stringify(b));
    // Strip OUTSIDE references — anything in connections.inputs/
    // outputs that doesn't resolve to a block in the snapshot, AND
    // any runtime-ref field pointing OUTSIDE.
    cloned.connections = {
      inputs: (cloned.connections?.inputs ?? []).filter(id => copyableIds.has(id)),
      outputs: (cloned.connections?.outputs ?? []).filter(id => copyableIds.has(id)),
    };
    const refFields = RUNTIME_REF_FIELDS_FOR_CLIPBOARD[String(cloned.type)] ?? EMPTY;
    const data = cloned.data as Record<string, unknown> | undefined;
    if (data) {
      for (const field of refFields) {
        const ptr = data[field];
        if (typeof ptr === 'string' && !copyableIds.has(ptr)) {
          delete data[field];
        }
      }
    }
    return cloned;
  });

  return { blocks, originalIds: copyable.map(b => b.id) };
}

/** Materialize a clipboard payload into a fresh set of pasted
 *  blocks ready to splice into the strategy. Generates new UUIDs,
 *  remaps every internal cross-reference (connections + runtime
 *  refs) to the new ids, applies the position offset, and appends
 *  " (copy)" to data.name when present so the user can see the
 *  pasted block in the editor without confusing it with the source. */
export function materializePaste(
  payload: ClipboardPayload,
  options: {
    /** Generates a fresh id for each pasted block. Injected for
     *  testability — production code passes uuidv4. */
    newId: () => string;
    /** Applied to every block's position. Default is +20,+20 so
     *  the pasted copy doesn't fully overlap the source (matches
     *  handleDuplicateSelected). */
    offset?: { x: number; y: number };
  },
): Block[] {
  const offset = options.offset ?? { x: 20, y: 20 };

  // Build the id-remap table FIRST so connections/refs that point
  // forward in the array (block B references block A that hasn't
  // been visited yet) still resolve cleanly.
  const idMap = new Map<string, string>();
  for (const b of payload.blocks) idMap.set(b.id, options.newId());

  return payload.blocks.map((src) => {
    const newId = idMap.get(src.id)!;
    // Deep-clone again so we don't mutate the clipboard payload —
    // repeated pastes should produce repeated independent results.
    const next: Block = JSON.parse(JSON.stringify(src));
    next.id = newId;
    next.position = {
      x: (src.position?.x ?? 0) + offset.x,
      y: (src.position?.y ?? 0) + offset.y,
    };
    // Remap edge ids.
    next.connections = {
      inputs: (next.connections?.inputs ?? []).map(id => idMap.get(id) ?? id),
      outputs: (next.connections?.outputs ?? []).map(id => idMap.get(id) ?? id),
    };
    // Remap runtime-ref ids. Any pointer that DOESN'T resolve in
    // idMap was an external reference that survived (shouldn't
    // happen — sanitizeBlocksForClipboard strips those — but
    // guard belt-and-suspenders by clearing it rather than
    // leaving a stale id).
    const refFields = RUNTIME_REF_FIELDS_FOR_CLIPBOARD[String(next.type)] ?? EMPTY;
    const data = next.data as Record<string, unknown> | undefined;
    if (data) {
      for (const field of refFields) {
        const ptr = data[field];
        if (typeof ptr === 'string') {
          const remapped = idMap.get(ptr);
          if (remapped) data[field] = remapped;
          else delete data[field];
        }
      }
      // Append " (copy)" to data.name (if present). Helps the user
      // see which block is the newcomer in the BlockEditor name
      // input. We DON'T touch data.title (config) since the
      // strategy_config singleton is never in the clipboard
      // anyway, but the same pattern would apply if it ever was.
      if (typeof data.name === 'string' && data.name.length > 0) {
        data.name = `${data.name} (copy)`;
      }
    }
    // Fresh metadata so the pasted block looks "new" in the
    // strategy. Same convention as handleDuplicateSelected.
    next.metadata = {
      ...(next.metadata ?? {}),
      version: next.metadata?.version ?? '1.0.0',
      created: new Date(),
      modified: new Date(),
      locked: false,
    };
    return next;
  });
}

/** Convenience predicate — does this event target an editable
 *  element where browser-native copy/paste should be allowed to
 *  proceed instead of being hijacked by our block clipboard?
 *  Used by the keydown handler to gate Ctrl+C/X/V interception.
 *  Pre-S8g the handler preventDefault'd Ctrl+C/V globally, which
 *  silently broke text copy/paste inside the BlockEditor's form
 *  fields. This helper restores native behavior in those fields
 *  while keeping block-level shortcuts working when the canvas
 *  itself has focus. */
export function isEditableTarget(target: EventTarget | null): boolean {
  // Node / SSR / non-browser test bundle: HTMLElement is undefined.
  // Short-circuit to false — there are no editable elements to
  // protect outside the browser; the keydown gating only matters
  // when this runs in the live UI.
  if (typeof HTMLElement === 'undefined') return false;
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  // Monaco editor's wrapping div carries `monaco-editor` in its
  // className tree. Anything inside it should keep native copy/paste.
  if (target.closest('.monaco-editor')) return true;
  return false;
}
